import React, { useState, useEffect, useRef } from 'react';
import ComponentExportButton from './ComponentExportButton';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Users,
  Lock,
  Unlock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Info,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface SODAnalysisProps {
  opportunityId: number;
  projectId: number;
  affectedSteps: any[];
  solutionApproach: string;
  viewMode?: 'all' | 'business' | 'compliance';
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  onApprovalChange?: (status: 'approved' | 'rejected') => void;
}

interface SODViolation {
  violationType: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  description: string;
  impactedRoles: string[];
  incompatibleDuties: {
    duty1: string;
    duty2: string;
    reason: string;
  };
  example: string;
  businessImpact: string;
  regulatoryRisk: string;
}

interface CompensatingControl {
  controlType: string;
  description: string;
  implementation: string;
  effectiveness: 'Strong' | 'Moderate' | 'Weak';
  cost: 'Low' | 'Medium' | 'High';
  priority: number;
}

interface SODAnalysisData {
  hasViolations: boolean;
  overallRisk: 'Critical' | 'High' | 'Medium' | 'Low' | 'None';
  violations: SODViolation[];
  roleConsolidation: {
    beforeAutomation: {
      role: string;
      responsibilities: string[];
    }[];
    afterAutomation: {
      role: string;
      responsibilities: string[];
      consolidatedDuties: string[];
    }[];
  };
  compensatingControls: CompensatingControl[];
  recommendations: {
    category: string;
    recommendation: string;
    rationale: string;
    effort: 'Low' | 'Medium' | 'High';
  }[];
  sodMatrix: {
    activity1: string;
    activity2: string;
    compatible: boolean;
    reason: string;
  }[];
}

const SODAnalysis: React.FC<SODAnalysisProps> = ({
  opportunityId,
  projectId,
  affectedSteps,
  solutionApproach,
  viewMode = 'all',
  approvalStatus = 'pending',
  onApprovalChange
}) => {
  const [loading, setLoading] = useState(true);
  const [sodData, setSODData] = useState<SODAnalysisData | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(() => {
    if (viewMode === 'compliance') return true;
    if (viewMode === 'business') return false;
    return false;
  });
  const [currentApprovalStatus, setCurrentApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>(approvalStatus || 'pending');
  const [updatingApproval, setUpdatingApproval] = useState(false);

  useEffect(() => {
    loadSODAnalysis();
  }, [opportunityId, projectId]);

  useEffect(() => {
    if (approvalStatus) {
      setCurrentApprovalStatus(approvalStatus);
    }
  }, [approvalStatus]);

  const loadSODAnalysis = async () => {
    try{
      setLoading(true);
      const response = await api.post(
        `/api/process-optimization/${projectId}/opportunities/${opportunityId}/sod-analysis`,
        { affectedSteps, solutionApproach }
      );
      setSODData(response.data);
      if (response.data.hasViolations && response.data.overallRisk === 'Critical') {
        setExpanded(true);
      }
    } catch (error) {
      console.error('Failed to load SOD analysis:', error);
      toast.error('Failed to load SOD analysis');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalChange = async (status: 'approved' | 'rejected' | 'pending') => {
    try {
      setUpdatingApproval(true);
      await api.patch(
        `/api/process-optimization/${projectId}/opportunities/${opportunityId}/sod-approval`,
        { status }
      );
      setCurrentApprovalStatus(status);
      if (status !== 'pending') {
        onApprovalChange?.(status);
      }
      toast.success(`SOD analysis ${status === 'pending' ? 'reset to pending' : status}`);
    } catch (error) {
      console.error('Failed to update approval status:', error);
      toast.error('Failed to update approval status');
    } finally {
      setUpdatingApproval(false);
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Critical':
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-red-500', icon: 'text-red-500' };
      case 'High':
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-orange-500', icon: 'text-orange-500' };
      case 'Medium':
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-yellow-500', icon: 'text-yellow-500' };
      case 'Low':
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-blue-500', icon: 'text-blue-500' };
      default:
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-green-500', icon: 'text-green-500' };
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
          <span className="text-gray-600">Analyzing Segregation of Duties...</span>
        </div>
      </div>
    );
  }

  if (!sodData) {
    return null;
  }

  const colors = getRiskColor(sodData.overallRisk);

  return (
    <div ref={exportRef} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-4 flex-1 hover:opacity-80 transition-opacity"
          >
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <Shield className={`w-5 h-5 ${colors.icon}`} />
            </div>
            <div className="text-left">
              <h3 className={`text-sm font-bold ${colors.text} flex items-center gap-2`}>
                Segregation of Duties (SOD) Analysis
                {sodData.hasViolations && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full border border-red-300 ">
                    {sodData.violations.length} Violation{sodData.violations.length !== 1 ? 's' : ''} Detected
                  </span>
                )}
              </h3>
              <p className="text-xs text-gray-600">
                {sodData.hasViolations
                  ? 'SOD conflicts require mitigation controls'
                  : 'No SOD violations detected'}
              </p>
            </div>
          </button>

          <div className="flex items-center gap-3">
            <ComponentExportButton targetRef={exportRef} filename={`sod-analysis-${opportunityId}`} />
            {/* Approval Status & Buttons */}
            {currentApprovalStatus === 'pending' ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleApprovalChange('approved')}
                  disabled={updatingApproval}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                  title="Approve SOD analysis"
                >
                  <Check className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={() => handleApprovalChange('rejected')}
                  disabled={updatingApproval}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                  title="Reject SOD analysis"
                >
                  <X className="w-4 h-4" />
                  Reject
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                  currentApprovalStatus === 'approved'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-red-100 text-red-700 border border-red-300'
                }`}>
                  {currentApprovalStatus === 'approved' ? (
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4" /> Approved
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <X className="w-4 h-4" /> Rejected
                    </span>
                  )}
                </span>
                <button
                  onClick={() => handleApprovalChange('pending')}
                  disabled={updatingApproval}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Reset
                </button>
              </div>
            )}

            {sodData.hasViolations ? (
              <div className={`px-3 py-1.5 ${colors.badge} text-white rounded-md`}>
                <div className="text-[9px] uppercase tracking-wide opacity-75 font-medium">Risk</div>
                <div className="text-xs font-bold">{sodData.overallRisk}</div>
              </div>
            ) : (
              <div className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span className="text-xs font-bold">Clear</span>
              </div>
            )}

            <button onClick={() => setExpanded(!expanded)} className="hover:opacity-70 transition-opacity">
              {expanded ? (
                <ChevronUp className={`w-4 h-4 ${colors.icon}`} />
              ) : (
                <ChevronDown className={`w-4 h-4 ${colors.icon}`} />
              )}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-6 pt-0 space-y-6">
          {sodData.violations.length > 0 && (
            <div className="space-y-4">
              <h4 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Detected SOD Violations
              </h4>
              {sodData.violations.map((violation, idx) => (
                <div key={idx} className="bg-white rounded-lg border border-red-200 p-4 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-3 py-1 text-xs font-bold rounded-full border ${getSeverityColor(violation.severity)}`}>
                          {violation.severity} Severity
                        </span>
                        <span className="text-sm font-semibold text-gray-700">{violation.violationType}</span>
                      </div>
                      <p className="text-sm font-semibold text-red-900 mb-2">{violation.description}</p>
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-3 mb-3 border border-red-200">
                    <div className="font-semibold text-sm text-red-900 mb-2">Incompatible Duties:</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-start gap-2">
                        <Lock className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-medium text-red-900">{violation.incompatibleDuties.duty1}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Lock className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-medium text-red-900">{violation.incompatibleDuties.duty2}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-red-200">
                      <span className="text-xs text-red-700">
                        <strong>Why Incompatible:</strong> {violation.incompatibleDuties.reason}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-gray-700">Impacted Roles:</strong>{' '}
                        <span className="text-gray-900">{violation.impactedRoles.join(', ')}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-gray-700">Example:</strong>{' '}
                        <span className="text-gray-900">{violation.example}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-gray-700">Business Impact:</strong>{' '}
                        <span className="text-gray-900">{violation.businessImpact}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <strong className="text-gray-700">Regulatory Risk:</strong>{' '}
                        <span className="text-gray-900">{violation.regulatoryRisk}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {sodData.roleConsolidation && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Role Consolidation Analysis
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-2">Before Automation</div>
                  <div className="space-y-2">
                    {sodData.roleConsolidation.beforeAutomation.map((role, idx) => (
                      <div key={idx} className="bg-gray-50 rounded p-2 border border-gray-200">
                        <div className="font-semibold text-sm text-gray-900">{role.role}</div>
                        <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                          {role.responsibilities.map((resp, ridx) => (
                            <li key={ridx} className="flex items-start gap-1">
                              <span className="text-blue-600">•</span>
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-2">After Automation</div>
                  <div className="space-y-2">
                    {sodData.roleConsolidation.afterAutomation.map((role, idx) => (
                      <div key={idx} className={`rounded p-2 border ${
                        role.consolidatedDuties.length > 0
                          ? 'bg-orange-50 border-orange-300'
                          : 'bg-green-50 border-green-200'
                      }`}>
                        <div className="font-semibold text-sm text-gray-900">{role.role}</div>
                        <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                          {role.responsibilities.map((resp, ridx) => (
                            <li key={ridx} className="flex items-start gap-1">
                              <span className="text-blue-600">•</span>
                              {resp}
                            </li>
                          ))}
                        </ul>
                        {role.consolidatedDuties.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-orange-200">
                            <div className="text-xs font-semibold text-orange-900 mb-1">
                              ⚠️ Consolidated Duties:
                            </div>
                            <ul className="text-xs text-orange-800 space-y-0.5">
                              {role.consolidatedDuties.map((duty, didx) => (
                                <li key={didx}>• {duty}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {sodData.compensatingControls.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Recommended Compensating Controls
              </h4>
              <div className="space-y-3">
                {sodData.compensatingControls
                  .sort((a, b) => a.priority - b.priority)
                  .map((control, idx) => (
                    <div key={idx} className="bg-green-50 rounded-lg p-3 border border-green-200">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-green-600 text-white text-xs font-bold rounded">
                            Priority {control.priority}
                          </span>
                          <span className="font-semibold text-sm text-green-900">{control.controlType}</span>
                        </div>
                        <div className="flex gap-2">
                          <span className={`px-2 py-1 text-xs font-bold rounded ${
                            control.effectiveness === 'Strong' ? 'bg-green-100 text-green-700' :
                            control.effectiveness === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {control.effectiveness}
                          </span>
                          <span className={`px-2 py-1 text-xs font-bold rounded ${
                            control.cost === 'Low' ? 'bg-green-100 text-green-700' :
                            control.cost === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {control.cost} Cost
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-900 mb-2">{control.description}</p>
                      <div className="bg-white rounded p-2 border border-green-200">
                        <div className="text-xs font-semibold text-gray-700 mb-1">Implementation:</div>
                        <p className="text-xs text-gray-700">{control.implementation}</p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {sodData.recommendations.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-purple-600" />
                Implementation Recommendations
              </h4>
              <div className="space-y-2">
                {sodData.recommendations.map((rec, idx) => (
                  <div key={idx} className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-semibold text-sm text-purple-900">{rec.category}</span>
                      <span className={`px-2 py-1 text-xs font-bold rounded ${
                        rec.effort === 'Low' ? 'bg-green-100 text-green-700' :
                        rec.effort === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {rec.effort} Effort
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 mb-1">{rec.recommendation}</p>
                    <p className="text-xs text-gray-600 italic">{rec.rationale}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sodData.sodMatrix.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                SOD Compatibility Matrix
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-indigo-100">
                      <th className="p-2 text-left font-semibold text-indigo-900">Activity 1</th>
                      <th className="p-2 text-left font-semibold text-indigo-900">Activity 2</th>
                      <th className="p-2 text-center font-semibold text-indigo-900">Compatible</th>
                      <th className="p-2 text-left font-semibold text-indigo-900">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sodData.sodMatrix.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-2 text-gray-900">{row.activity1}</td>
                        <td className="p-2 text-gray-900">{row.activity2}</td>
                        <td className="p-2 text-center">
                          {row.compatible ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600 mx-auto" />
                          )}
                        </td>
                        <td className="p-2 text-gray-700 text-xs">{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SODAnalysis;
